<script lang="ts" setup>
import userStore from '@/store/UserStore'
import PriceInput from '@/components/PriceInput.vue'
import { camelize, ValidatorType } from '@/helper/ValidateDirective'
import appStore from '@/store/AppStore'
import PriceCalculator from '@/components/steps/summary/PriceCalculator.vue'
import { computed } from 'vue'
import { ValidationRuleType } from '@/helper/Types'
import { calculateDisplayPrice, copyToClipboard, formatPrice } from '@/helper/tools'
import { ProductResponse } from '@/services/models/ProductResponse'
import { Product } from '@/services/models/implementations/Product'

const props = withDefaults(
  defineProps<{
    enableCustomPrices?: boolean
    price?: number
    product: ProductResponse | Product
    productType: string
    productTitle: string
    productSpecs: { [key: string]: string }
    isPackageProduct?: boolean
    quantity?: number
    validator: ValidatorType
  }>(),
  {
    price: undefined,
    quantity: () => 1
  }
)

const emit = defineEmits<{
  (e: 'update:price', val: number | undefined): void
  (e: 'confirm', val: string | number | undefined): void
  (e: 'blur'): void
}>()

const quantityVal = computed(() => {
  return props.quantity
})

const priceVal = computed({
  get() {
    return props.price
  },
  set(val: number | undefined) {
    emit('update:price', val)
  }
})

const productQuantity = computed((): string => {
  return props.quantity > 1 ? `${props.quantity}x` : ''
})

defineExpose({
  validator: props.validator
})
</script>

<template>
  <div
    :class="`${productType}-item`"
    :data-id="product.id"
    class="summary-product item"
    @click.alt.prevent="copyToClipboard(product.id)"
  >
    <div class="item__content">
      <div class="image-box item__image-box is--zoomed has--brand-bg">
        <div :class="!product.pictureUrl && 'fallback-bg'" class="image-box__container">
          <img v-if="product.pictureUrl" :alt="product.name" :src="product.pictureUrl" loading="lazy">
        </div>
      </div>

      <div class="item__description">
        <div v-if="quantityVal > 1 && productType === 'heat-pump'" class="item__cascade-container">
          <div
            :class="{
              'brand-va': userStore.brand === 'VAILLANT',
              'brand-sd': userStore.brand === 'SAUNIERDUVAL'
            }"
            class="item__cascade-tag"
          >
            <i class="ic-connected" />
            {{ $t('step_heat_pumps.badge_cascade_title') }}
          </div>
        </div>

        <div class="item__title">
          {{ productTitle }}
        </div>

        <div class="item__name">
          {{ productQuantity }} {{ product.name }}
        </div>

        <div class="item__specs">
          <div class="item__data-container">
            <div
              v-for="(spec, key) in productSpecs"
              :key="key"
              :class="`data-${key.replace(/([A-Z])([a-z])/g, '-$1$2').toLowerCase()}`"
              class="item__data"
            >
              {{ spec }}
            </div>

            <div v-if="!isPackageProduct" class="item__data order-number">
              <b>{{ $t('step_accessories.table_order_number') }}:</b>
              {{ product.orderNumber }}
            </div>
          </div>
        </div>

        <div
          v-if="!isPackageProduct && !userStore.hidePrices"
          :class="{ 'is--custom-price': enableCustomPrices }"
          class="item__price-container"
        >
          <div v-if="enableCustomPrices" class="item__custom-price">
            <div class="custom-price-info">
              {{ $t('step_summary.custom_price_legal_info') }}
            </div>

            <div class="custom-price__input-container">
              <PriceInput
                :id="`custom-price-${product.id}`"
                v-model:price="priceVal"
                v-validate="ValidationRuleType.POSITIVE_ZERO_DECIMAL"
                :disabled="appStore.isLoading"
                :error="validator[camelize(`custom-price-${product.id}`)]?.error"
                :original-price="product.price * quantityVal"
                @blur="$emit('blur')"
              />

              <PriceCalculator
                :id="product.id"
                :original-price="product.price"
                @confirm="newPrice => $emit('confirm', newPrice)"
              />
            </div>

            <div class="item__custom-price-infos">
              <div>{{ $t('step_summary.list_price') }}: {{ formatPrice(product.price * quantityVal) }}</div>
              <div>{{ $t('step_summary.table_vat_excluded') }}</div>
            </div>
          </div>

          <div v-else>
            <div class="item__price">
              {{ calculateDisplayPrice(product.price, quantityVal) }}
            </div>

            <div v-if="userStore.vatRate && userStore.enableDisplayOfVatPrices" class="item__vat">
              {{ $t('step_summary.table_vat_included', { vat: userStore.vatRate }) }}
            </div>

            <div v-else class="item__vat">
              {{ $t('step_summary.table_vat_excluded') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.summary-product {
  .price-input {
    margin-right: rem-calc(8);
  }

  .price-input__formatted-price {
    font-weight: bold;
  }

  .item__cascade {
    &-container {
      display: flex;
      align-content: center;
      margin-bottom: rem-calc(8);
    }

    &-tag {
      display: flex;
      align-items: center;
      padding: 8px 10px;
      color: white;
      font-size: rem-calc(14);
      line-height: rem-calc(15);

      &.brand-va {
        @include themify($themes) {
          background: themed('primaryColor');
        }
      }

      &.brand-sd {
        @include themify($themes) {
          background: themed('secondaryColor');
        }
      }

      @include themify($themes) {
        border-radius: rem-calc(themed('btnBorderRadius'));
      }

      i {
        margin-right: 6px;
        font-size: rem-calc(16);
      }
    }
  }
}
</style>
